import { ShieldCheck, Lock, LineChart, Infinity as InfinityIcon, Award, Quote } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { FOUNDER, VALUES, OFFICES, IMAGES } from "@/lib/content";
import { MapPin } from "lucide-react";

const ICONS = { ShieldCheck, Lock, LineChart, Infinity: InfinityIcon };

const TIMELINE = [
  { year: "Tier-1 Banking", title: "Structured Products Sales", desc: "Over a decade inside private banking, working alongside the relationship managers and team heads we place today." },
  { year: "The Insight", title: "A structural gap", desc: "Most recruiters had never worked in banking. The people who identify banking talent should have built banking careers." },
  { year: "Beyond Potentials", title: "By bankers, for bankers", desc: "A boutique talent partner covering the full lifecycle, exclusively for Monaco & Geneva private banking." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        breadcrumb={[{ label: "About" }]}
        eyebrow="By bankers, for bankers"
        title="We've sat on your side of the table."
        intro="Beyond Potentials was founded on a simple conviction: that the people who identify private banking talent should have built private banking careers themselves."
        image={IMAGES.glass}
      />

      {/* Founder */}
      <section className="section-py" data-testid="founder-section">
        <div className="container-bp grid grid-cols-1 items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <div className="relative">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)]">
                <img src={IMAGES.founder} alt={FOUNDER.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bp-bg)]/60 to-transparent" />
              </div>
              <div className="absolute -bottom-5 -right-3 rounded-[var(--radius-md)] border border-[color:var(--bp-gold)]/40 bg-[color:var(--bp-surface)] px-5 py-4">
                <p className="font-display text-lg font-semibold text-[color:var(--bp-cream)]">{FOUNDER.name}</p>
                <p className="text-[12px] text-[color:var(--bp-gold)]">{FOUNDER.role}</p>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow="The Founder" title="Celine Corletti" />
            <div className="mt-3 flex flex-wrap gap-2">
              {FOUNDER.credentials.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] px-3 py-1.5 text-[12px] text-[color:var(--bp-text-muted)]">
                  <Award className="h-3.5 w-3.5 text-[color:var(--bp-gold)]" /> {c}
                </span>
              ))}
            </div>
            <div className="mt-6 space-y-4">
              {FOUNDER.bio.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}><p className="max-w-xl text-[15px] leading-[1.8] text-[color:var(--bp-text-muted)]">{p}</p></Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-py bg-[color:var(--bp-bg-2)]" data-testid="timeline-section">
        <div className="container-bp">
          <SectionHeading align="center" eyebrow="From banker to founder" title="A practitioner's path." />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {TIMELINE.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative h-full rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-7">
                  <span className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-[color:var(--bp-teal)]">{t.year}</span>
                  <h3 className="mt-3 font-display text-xl font-semibold text-[color:var(--bp-cream)]">{t.title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-[color:var(--bp-text-muted)]">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-py" data-testid="values-section">
        <div className="container-bp">
          <SectionHeading eyebrow="Operating principles" title="How we work, and why it's different." />
          <RevealGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {VALUES.map((v) => {
              const Icon = ICONS[v.icon] || ShieldCheck;
              return (
                <RevealItem key={v.title}>
                  <div className="flex h-full items-start gap-5 rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--bp-teal)]/50">
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[color:var(--bp-teal)]/40 bg-[color:var(--bp-teal-soft)] text-[color:var(--bp-teal)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-semibold text-[color:var(--bp-cream)]">{v.title}</h3>
                      <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--bp-text-muted)]">{v.desc}</p>
                    </div>
                  </div>
                </RevealItem>
              );
            })}
          </RevealGroup>
        </div>
      </section>

      {/* Media + Offices */}
      <section className="section-py bg-[color:var(--bp-cream)]" data-testid="offices-section">
        <div className="container-bp grid grid-cols-1 gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading tone="light" eyebrow="Recognition" title="A trusted voice in the market." intro="Our perspective on Monaco and Geneva banking talent has featured across leading financial media and industry platforms." />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                { img: IMAGES.mediaBFM, title: "BFM Business", caption: "Les talents dans la finance" },
                { img: IMAGES.mediaCFA, title: "CFA Society Switzerland", caption: "Keynote speaker" },
              ].map((m) => (
                <Reveal key={m.title}>
                  <figure className="overflow-hidden rounded-[var(--radius-md)] border border-[#E6E0D4] bg-white shadow-[0_12px_30px_rgba(10,31,61,0.08)]">
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={m.img} alt={`${m.title} — ${m.caption}`} className="h-full w-full object-cover" loading="lazy" />
                    </div>
                    <figcaption className="px-4 py-3">
                      <p className="text-[13px] font-semibold text-[color:var(--bp-text-on-light)]">{m.title}</p>
                      <p className="text-[12px] text-[color:var(--bp-text-body-on-light)]">{m.caption}</p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {["Les Echos", "Bilan", "Monaco For Finance", "IUM Career Week"].map((m) => (
                <span key={m} className="inline-flex items-center gap-2 rounded-full border border-[#E2DCCF] bg-white px-4 py-2 text-[13px] font-medium text-[color:var(--bp-text-on-light)]">
                  <Quote className="h-3.5 w-3.5 text-[color:var(--bp-teal)]" /> {m}
                </span>
              ))}
            </div>
          </div>
          <div>
            <SectionHeading tone="light" eyebrow="Where we are" title="Genuine local presence." />
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {OFFICES.map((o) => (
                <Reveal key={o.city}>
                  <div className="h-full rounded-[var(--radius-md)] border border-[#E6E0D4] bg-white p-5">
                    <p className="flex items-center gap-1.5 font-display text-lg font-semibold text-[color:var(--bp-text-on-light)]">
                      <MapPin className="h-4 w-4 text-[color:var(--bp-teal)]" /> {o.city}
                    </p>
                    <div className="mt-3 space-y-0.5 text-[13px] text-[color:var(--bp-text-body-on-light)]">
                      {o.lines.map((l) => <p key={l}>{l}</p>)}
                      <p className="pt-2 text-[color:var(--bp-teal)]">{o.phone}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand title="Work with a partner who has done the job." />
    </div>
  );
}
